interface GoogleAnalyticsEventObj {
    name?: string;
    version?: string;
    event_category?: string;
    event_action?: string;
    event_label?: string;
    page_path?: string;
    page_location?: string;
    create_list?: {
        custom?: {
            [key: string]: any;
        };
    };
    custom?: {
        [key: string]: any;
    };
    params?: {
        items?: Array<
            { id: string; quantity: number } & Record<string, string | number>
        >;
        [key: string]: any;
    };
}

declare const window: Window & {
    sendEvent?: (eventObj: GoogleAnalyticsEventObj) => void;
};

interface LogAddItemsToFavouritesProps {
    source: string;
    listId: string;
    isNewList: boolean;
    items: Array<{
        itemNo: string;
        quantity: number;
        type: string;
    }>;
    isSavedForLater?: boolean;
    ga?: GoogleAnalyticsEventObj['custom'] & GoogleAnalyticsEventObj['params'];
}

const sendEventWithDebug = (
    options: GoogleAnalyticsEventObj,
    type = 'logEvent'
) => {
    if (localStorage && localStorage?.getItem('debugFav')) {
        console.log(
            `DEBUG MODE: ${type}() is sending window.sendEvent(options)`,
            options
        );
    }
    typeof window.sendEvent === 'function' && window.sendEvent(options);
};

export const logAddItemsToFavourites = ({
    source,
    listId,
    items,
    isNewList,
    isSavedForLater,
    ga,
}: LogAddItemsToFavouritesProps) => {
    // Concatenate 'itemNos', 'types' and 'quantities' as strings. E.g.:
    // itemNos = 12345678,34567890
    // types = ART,SPR
    // quantities = 2,1
    const { itemNos, types, quantities } = items.reduce(
        (acc, curr) => ({
            itemNos: acc.itemNos
                ? `${acc.itemNos},${curr.itemNo}`
                : curr.itemNo,
            types: acc.types ? `${acc.types},${curr.type}` : curr.type,
            quantities: acc.quantities
                ? `${acc.quantities},${curr.quantity}`
                : String(curr.quantity),
        }),
        { itemNos: '', types: '', quantities: '' }
    );

    sendEventWithDebug({
        event_category: 'ecommerce',
        event_action: 'add_to_wishlist',
        event_label: source,
        custom: {
            list_id: listId,
            item_number_event: itemNos,
            item_type_event: types,
            product_quantity: quantities,
            new_list: isNewList,
            save_for_later: isSavedForLater,
            ...ga?.custom,
        },
        params: {
            items: items.map((item, index) => {
                let extendedItem: NonNullable<
                    NonNullable<GoogleAnalyticsEventObj['params']>['items']
                >[0] = {
                    id: item.itemNo,
                    quantity: item.quantity,
                };
                // Append attributes sent from PubSub event if they exist
                if (ga?.params?.items) {
                    extendedItem = {
                        ...ga.params.items[index],
                        ...extendedItem,
                    };
                }
                return extendedItem;
            }),
        },
    });
};

export const logRemoveItemFromFavourites = ({
    source,
    listId,
    productNumber,
    productType,
    quantity,
    ga,
}: {
    source: string;
    listId: string;
    productNumber: string;
    productType: string;
    quantity: number;
    ga?: {
        custom: any;
    };
}) =>
    sendEventWithDebug({
        event_category: 'ecommerce',
        event_action: 'remove_from_wishlist',
        event_label: source,
        custom: {
            list_id: listId,
            item_number_event: productNumber,
            item_type_event: productType,
            product_quantity: quantity,
            ...ga?.custom,
        },
    });

export const logEvent = (category: string, action: string, label: string) =>
    sendEventWithDebug({
        event_category: category,
        event_action: action,
        event_label: label,
    });

export const logCreateList = (listId: string) =>
    sendEventWithDebug({
        event_category: 'ecommerce',
        event_action: 'create_list',
        create_list: {
            custom: { list_id: listId },
        },
    });
